import React, { useEffect, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { useCheckStatus, useGetAccessToken, KycVerificationType } from '@/api/queryHooks/useKycController';
import { KycStatus } from '@/api/common.types';
import { detectRestrictedBrowser } from '@/utils/detectRestrictedBrowser';
// Import the concrete module (not the popups barrel) to avoid an import cycle:
// popups/index -> VerifyYourIdentity -> Sumsub.
import { LockdownModeWarning } from '@/components/common/popups/LockdownModeWarning';

interface SumsubProps {
  onInit: () => void;
  onComplete: () => void;
  verificationId: string;
  onError?: () => void;
}

// https://docs.sumsub.com/reference/websdk
export const Sumsub: React.FC<SumsubProps> = ({ onInit, onComplete, verificationId, onError }) => {
  const { mutateAsync: getAccessToken } = useGetAccessToken();
  const { data } = useCheckStatus();
  // eslint-disable-next-line no-spaced-func, func-call-spacing
  const sdkInstanceRef = useRef<{ destroy: () => void } | null>(null);
  // null = detection not run yet (don't init), true = restricted (show guidance), false = ok.
  const [isRestricted, setIsRestricted] = useState<boolean | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const isBiometric = data?.status === KycStatus.BIOMETRIC_REQUIRED;
  const verificationType: KycVerificationType = isBiometric ? 'BUY_CRYPTO' : 'REGISTRATION';

  async function fetchAccessToken(): Promise<string> {
    const result = await getAccessToken({ verificationType, verificationId });
    return result.token;
  }

  async function sdkInit() {
    let accessToken: string;
    try {
      accessToken = await fetchAccessToken();
    } catch (e) {
      datadogLogs.logger.error('SumsubAccessTokenError', { verificationType, verificationId, error: String(e) });
      onError?.();
      // eslint-disable-next-line no-console
      return console.error(e);
    }

    const snsWebSdk = (await import('@sumsub/websdk')).default;

    const snsWebSdkInstance = snsWebSdk
      .init(accessToken, () => fetchAccessToken())
      .withConf({
        lang: 'en',
        theme: 'light',
        uiConf: {
          customBodyClass: 'w-full',
        },
      })
      .withOptions({ addViewportTag: false, adaptIframeHeight: true })
      .on('idCheck.onApplicantSubmitted', () => {
        onComplete();
      })
      .on('idCheck.onApplicantActionCompleted', () => {
        onComplete();
      })
      .on('idCheck.onError', (error: any) => {
        datadogLogs.logger.error('SumsubSdkError', { verificationType, verificationId, error });
        // eslint-disable-next-line no-console
        console.error('Sumsub SDK error:', error);
        onError?.();
      })
      .build();

    snsWebSdkInstance.launch('#sumsubContainerId');
    sdkInstanceRef.current = snsWebSdkInstance;
    return onInit();
  }

  // Detect restricted browsers (e.g. iOS Lockdown Mode) that can't run the camera/WASM
  // liveness flow, before we attempt to launch the SDK. Re-runs when the user taps retry.
  useEffect(() => {
    const result = detectRestrictedBrowser();
    setIsRestricted(result.isRestricted);
    if (result.isRestricted) {
      datadogLogs.logger.warn('RestrictedBrowserDetected', {
        reasons: result.reasons,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        verificationType,
      });
    }
  }, [retryNonce, verificationType]);

  useEffect(() => {
    if (isRestricted !== false) return undefined; // wait for detection; skip launch when restricted
    sdkInstanceRef.current?.destroy(); // destroy before re-init
    sdkInit();
    return () => {
      sdkInstanceRef.current?.destroy();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationId, verificationType, isRestricted]);

  if (isRestricted === true) {
    return <LockdownModeWarning onRetry={() => setRetryNonce((nonce) => nonce + 1)} />;
  }

  return <div id="sumsubContainerId" className="w-full h-full"></div>;
};
